import React, { useState } from 'react';
import { Calendar, Map, List, Clock, MapPin, User, MoreVertical, Search, Filter, ChevronDown, AlertCircle, CheckCircle, PlayCircle, Package, ArrowRight } from 'lucide-react';
import { JobsMap } from './jobs/JobsMap';
import { JobsMapPanel } from './jobs/JobsMapPanel';
// Add TeamMember interface
export interface TeamMember {
  id: string;
  name: string;
  initials: string;
  color: string;
  textColor: string;
  currentLat: number;
  currentLng: number;
}
interface Job {
  id: number;
  timeStart: string;
  timeEnd: string;
  address: string;
  city: string;
  host: string;
  type: 'Pulizia Standard' | 'Pulizia Profonda' | 'Check-out';
  cleaner: {
    name: string;
    avatarColor: string;
    initials: string;
  };
  package?: {
    name: string;
    id: string;
  };
  status: 'scheduled' | 'in-progress' | 'completed' | 'issue';
  earnings: string;
  lat: number;
  lng: number;
  cleanerId?: string; // Add cleanerId to link jobs to team members
}
export function MyJobsPage() {
  const [viewMode, setViewMode] = useState<'list' | 'calendar' | 'map'>('map');
  const [activeTab, setActiveTab] = useState<'today' | 'week' | 'completed' | 'all'>('today');
  const [searchQuery, setSearchQuery] = useState('');
  // Map state
  const [activeJobId, setActiveJobId] = useState<number | null>(null);
  const [selectedMemberId, setSelectedMemberId] = useState<string | null>(null);
  const [isPanelCollapsed, setIsPanelCollapsed] = useState(false);
  // Mock Team Members
  const teamMembers: TeamMember[] = [{
    id: 'tm-1',
    name: 'Lucia M.',
    initials: 'LM',
    color: '#fce7f3',
    textColor: '#be185d',
    currentLat: 41.908,
    currentLng: 12.467
  }, {
    id: 'tm-2',
    name: 'Marco R.',
    initials: 'MR',
    color: '#dbeafe',
    textColor: '#1d4ed8',
    currentLat: 41.9015,
    currentLng: 12.476
  }, {
    id: 'tm-3',
    name: 'Alessandro V.',
    initials: 'AV',
    color: '#dcfce7',
    textColor: '#15803d',
    currentLat: 41.89,
    currentLng: 12.495
  }, {
    id: 'tm-4',
    name: 'Francesca N.',
    initials: 'FN',
    color: '#ffedd5',
    textColor: '#c2410c',
    currentLat: 41.9055,
    currentLng: 12.482
  }];
  // Mock Data - Updated with cleanerId
  const jobs: Job[] = [{
    id: 1,
    timeStart: '10:00',
    timeEnd: '12:00',
    address: 'Via Roma 4',
    city: 'Roma',
    host: 'Marco B.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Lucia M.',
      avatarColor: 'bg-pink-100 text-pink-700',
      initials: 'LM'
    },
    cleanerId: 'tm-1',
    package: {
      name: 'Pacchetto Zona Prati',
      id: 'pkg-1'
    },
    status: 'in-progress',
    earnings: '€50',
    lat: 41.9085,
    lng: 12.4665
  }, {
    id: 2,
    timeStart: '13:00',
    timeEnd: '14:30',
    address: 'Corso Italia 22',
    city: 'Roma',
    host: 'Giulia R.',
    type: 'Check-out',
    cleaner: {
      name: 'Marco R.',
      avatarColor: 'bg-blue-100 text-blue-700',
      initials: 'MR'
    },
    cleanerId: 'tm-2',
    status: 'scheduled',
    earnings: '€45',
    lat: 41.903,
    lng: 12.479
  }, {
    id: 3,
    timeStart: '09:00',
    timeEnd: '11:00',
    address: 'Piazza Navona 5',
    city: 'Roma',
    host: 'Roberto F.',
    type: 'Pulizia Profonda',
    cleaner: {
      name: 'Alessandro V.',
      avatarColor: 'bg-green-100 text-green-700',
      initials: 'AV'
    },
    cleanerId: 'tm-3',
    status: 'completed',
    earnings: '€80',
    lat: 41.8992,
    lng: 12.4731
  }, {
    id: 4,
    timeStart: '15:00',
    timeEnd: '17:00',
    address: 'Via del Corso 10',
    city: 'Roma',
    host: 'Elena S.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Francesca N.',
      avatarColor: 'bg-orange-100 text-orange-700',
      initials: 'FN'
    },
    cleanerId: 'tm-4',
    status: 'issue',
    earnings: '€55',
    lat: 41.9058,
    lng: 12.4823
  }, {
    id: 5,
    timeStart: '11:30',
    timeEnd: '13:00',
    address: 'Via Condotti 8',
    city: 'Roma',
    host: 'Luigi P.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Lucia M.',
      avatarColor: 'bg-pink-100 text-pink-700',
      initials: 'LM'
    },
    cleanerId: 'tm-1',
    status: 'completed',
    earnings: '€40',
    lat: 41.906,
    lng: 12.481
  },
  // Added jobs for map richness
  {
    id: 6,
    timeStart: '08:00',
    timeEnd: '10:00',
    address: 'Via Cola di Rienzo 28',
    city: 'Roma',
    host: 'Paola G.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Marco R.',
      avatarColor: 'bg-blue-100 text-blue-700',
      initials: 'MR'
    },
    cleanerId: 'tm-2',
    status: 'completed',
    earnings: '€50',
    lat: 41.912,
    lng: 12.462
  }, {
    id: 7,
    timeStart: '14:00',
    timeEnd: '16:00',
    address: 'Viale Trastevere 45',
    city: 'Roma',
    host: 'Giovanni T.',
    type: 'Check-out',
    cleaner: {
      name: 'Lucia M.',
      avatarColor: 'bg-pink-100 text-pink-700',
      initials: 'LM'
    },
    cleanerId: 'tm-1',
    status: 'scheduled',
    earnings: '€60',
    lat: 41.8882,
    lng: 12.4708
  }, {
    id: 8,
    timeStart: '16:30',
    timeEnd: '18:30',
    address: 'Via Appia Nuova 100',
    city: 'Roma',
    host: 'Sara L.',
    type: 'Pulizia Standard',
    cleaner: {
      name: 'Alessandro V.',
      avatarColor: 'bg-green-100 text-green-700',
      initials: 'AV'
    },
    cleanerId: 'tm-3',
    status: 'scheduled',
    earnings: '€55',
    lat: 41.8868,
    lng: 12.5053
  }];
  // Stats for progress bar
  const totalToday = 8;
  const completedToday = 3;
  const progressPercentage = completedToday / totalToday * 100;
  const getStatusBadge = (status: Job['status']) => {
    switch (status) {
      case 'scheduled':
        return <span className="bg-gray-100 text-gray-600 rounded-full text-xs font-medium px-2.5 py-1 flex items-center gap-1.5" data-id="element-1157">
            <Clock size={12} data-id="element-1158" />
            Programmato
          </span>;
      case 'in-progress':
        return <span className="bg-blue-50 text-blue-600 border border-blue-100 rounded-full text-xs font-medium px-2.5 py-1 flex items-center gap-1.5 animate-pulse" data-id="element-1159">
            <PlayCircle size={12} data-id="element-1160" />
            In Corso
          </span>;
      case 'completed':
        return <span className="bg-green-50 text-green-600 border border-green-100 rounded-full text-xs font-medium px-2.5 py-1 flex items-center gap-1.5" data-id="element-1161">
            <CheckCircle size={12} data-id="element-1162" />
            Completato
          </span>;
      case 'issue':
        return <span className="bg-amber-50 text-amber-600 border border-amber-100 rounded-full text-xs font-medium px-2.5 py-1 flex items-center gap-1.5" data-id="element-1163">
            <AlertCircle size={12} data-id="element-1164" />
            Problema
          </span>;
    }
  };
  const getTypeBadgeColor = (type: Job['type']) => {
    switch (type) {
      case 'Pulizia Profonda':
        return 'bg-purple-50 text-purple-700 border-purple-100';
      case 'Check-out':
        return 'bg-indigo-50 text-indigo-700 border-indigo-100';
      default:
        return 'bg-teal-50 text-teal-700 border-teal-100';
    }
  };
  // --- Map View Render ---
  if (viewMode === 'map') {
    return <div className="relative h-[calc(100vh-64px)] overflow-hidden -m-4 lg:-m-8" data-id="element-1165">
        {/* Full Screen Map */}
        <div className="absolute inset-0 z-0" data-id="element-1166">
          <JobsMap jobs={jobs} activeJobId={activeJobId} onJobSelect={setActiveJobId} teamMembers={teamMembers} selectedMemberId={selectedMemberId} onMemberSelect={setSelectedMemberId} data-id="element-1167" />
        </div>

        {/* Floating Panel */}
        <JobsMapPanel jobs={jobs} activeJobId={activeJobId} onSelect={setActiveJobId} isCollapsed={isPanelCollapsed} onToggleCollapse={() => setIsPanelCollapsed(!isPanelCollapsed)} teamMembers={teamMembers} selectedMemberId={selectedMemberId} onMemberSelect={setSelectedMemberId} data-id="element-1168" />

        {/* View Toggle (Floating) */}
        <div className="absolute top-8 right-8 lg:top-12 lg:right-12 z-[500] bg-white p-1 rounded-lg shadow-md border border-gray-200 flex" data-id="element-1169">
          <button onClick={() => setViewMode('map')} className="px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 bg-navy text-white shadow-sm" data-id="element-1170">
            <Map size={16} data-id="element-1171" />
            <span className="hidden sm:inline" data-id="element-1172">Mappa</span>
          </button>
          <button onClick={() => setViewMode('list')} className="px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 text-gray-500 hover:text-navy hover:bg-gray-50 transition-colors" data-id="element-1173">
            <List size={16} data-id="element-1174" />
            <span className="hidden sm:inline" data-id="element-1175">Lista</span>
          </button>
          <button onClick={() => setViewMode('calendar')} className="px-3 py-2 rounded-md text-sm font-medium flex items-center gap-2 text-gray-500 hover:text-navy hover:bg-gray-50 transition-colors" data-id="element-1176">
            <Calendar size={16} data-id="element-1177" />
            <span className="hidden sm:inline" data-id="element-1178">Calendario</span>
          </button>
        </div>
      </div>;
  }
  // --- Standard List/Calendar View ---
  return <div className="space-y-6" data-id="element-1179">
      {/* Header Section */}
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4" data-id="element-1180">
        <div data-id="element-1181">
          <h1 className="text-xl lg:text-2xl font-bold font-montserrat text-navy mb-1" data-id="element-1182">
            I Miei Lavori
          </h1>
          <p className="text-sm text-gray-500" data-id="element-1183">
            Gestisci e monitora gli interventi assegnati
          </p>
        </div>

        {/* View Toggle (Pill/Segment style) */}
        <div className="bg-gray-100 p-1 rounded-lg flex self-start lg:self-auto" data-id="element-1184">
          <button onClick={() => setViewMode('map')} className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-all ${viewMode === 'map' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-1185">
            <Map size={16} data-id="element-1186" />
            Mappa
          </button>
          <button onClick={() => setViewMode('list')} className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-all ${viewMode === 'list' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-1187">
            <List size={16} data-id="element-1188" />
            Lista
          </button>
          <button onClick={() => setViewMode('calendar')} className={`px-4 py-2 rounded-md text-sm font-medium flex items-center gap-2 transition-all ${viewMode === 'calendar' ? 'bg-white text-navy shadow-sm' : 'text-gray-500 hover:text-navy'}`} data-id="element-1189">
            <Calendar size={16} data-id="element-1190" />
            Calendario
          </button>
        </div>
      </div>

      {/* Progress Summary Bar */}
      <div className="bg-white border border-gray-200 rounded-xl p-4 lg:p-5 flex flex-col md:flex-row items-center gap-4 md:gap-8" data-id="element-1191">
        <div className="flex-1 w-full" data-id="element-1192">
          <div className="flex justify-between items-end mb-2" data-id="element-1193">
            <div data-id="element-1194">
              <span className="text-sm font-semibold text-navy block" data-id="element-1195">
                Avanzamento Giornaliero
              </span>
              <span className="text-xs text-gray-500" data-id="element-1196">
                {completedToday} di {totalToday} completati oggi
              </span>
            </div>
            <span className="text-lg font-bold text-teal-deep" data-id="element-1197">
              {Math.round(progressPercentage)}%
            </span>
          </div>
          <div className="w-full bg-gray-100 h-2.5 rounded-full overflow-hidden" data-id="element-1198">
            <div className="bg-teal-deep h-full rounded-full transition-all duration-500 ease-out" style={{
            width: `${progressPercentage}%`
          }} data-id="element-1199"></div>
          </div>
        </div>
        <div className="flex gap-4 w-full md:w-auto border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-8" data-id="element-1200">
          <div className="text-center" data-id="element-1201">
            <div className="text-2xl font-bold text-navy" data-id="element-1202">2</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium" data-id="element-1203">
              In Corso
            </div>
          </div>
          <div className="text-center" data-id="element-1204">
            <div className="text-2xl font-bold text-amber-500" data-id="element-1205">1</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium" data-id="element-1206">
              Problemi
            </div>
          </div>
          <div className="text-center" data-id="element-1207">
            <div className="text-2xl font-bold text-gray-400" data-id="element-1208">2</div>
            <div className="text-[10px] uppercase tracking-wider text-gray-400 font-medium" data-id="element-1209">
              Da Iniziare
            </div>
          </div>
        </div>
      </div>

      {/* Filters & Tabs */}
      <div className="flex flex-col gap-4" data-id="element-1210">
        {/* Horizontal Tabs (Underline style) */}
        <div className="border-b border-gray-200 flex gap-6 overflow-x-auto" data-id="element-1211">
          {[{
          id: 'today',
          label: 'Oggi',
          count: 8
        }, {
          id: 'week',
          label: 'Questa Settimana',
          count: 42
        }, {
          id: 'completed',
          label: 'Completati',
          count: null
        }, {
          id: 'all',
          label: 'Tutti',
          count: null
        }].map(tab => <button key={tab.id} onClick={() => setActiveTab(tab.id as any)} className={`pb-3 text-sm font-medium border-b-2 transition-colors whitespace-nowrap flex items-center gap-2 ${activeTab === tab.id ? 'border-teal-deep text-teal-deep' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-id="element-1212">
              {tab.label}
              {tab.count && <span className={`text-[10px] px-1.5 py-0.5 rounded-full ${activeTab === tab.id ? 'bg-teal-100 text-teal-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-1213">
                  {tab.count}
                </span>}
            </button>)}
        </div>

        {/* Quick Filters Row */}
        <div className="flex flex-col md:flex-row gap-3 items-center justify-between" data-id="element-1214">
          <div className="relative w-full md:w-80" data-id="element-1215">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" data-id="element-1216" />
            <input type="text" placeholder="Cerca per proprietà o host..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-deep focus:border-transparent outline-none" data-id="element-1217" />
          </div>
          <div className="flex gap-3 w-full md:w-auto" data-id="element-1218">
            <button className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-navy transition-colors bg-white" data-id="element-1219">
              <User size={16} data-id="element-1220" />
              <span data-id="element-1221">Collaboratore</span>
              <ChevronDown size={14} className="text-gray-400" data-id="element-1222" />
            </button>
            <button className="flex-1 md:flex-none flex items-center justify-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-600 hover:bg-gray-50 hover:text-navy transition-colors bg-white" data-id="element-1223">
              <Filter size={16} data-id="element-1224" />
              <span data-id="element-1225">Stato</span>
              <ChevronDown size={14} className="text-gray-400" data-id="element-1226" />
            </button>
          </div>
        </div>
      </div>

      {/* Jobs List */}
      <div className="space-y-4" data-id="element-1227">
        {jobs.map(job => <div key={job.id} className={`bg-white border rounded-xl p-4 lg:p-5 transition-all hover:shadow-md ${job.status === 'in-progress' ? 'border-blue-200 ring-1 ring-blue-100 shadow-sm' : job.status === 'issue' ? 'border-amber-200 bg-amber-50/10' : 'border-gray-200'}`} data-id="element-1228">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 lg:gap-6 items-center" data-id="element-1229">
              {/* Left Side: Time & Location */}
              <div className="lg:col-span-4 space-y-3" data-id="element-1230">
                <div className="flex items-center gap-3" data-id="element-1231">
                  <div className={`px-2 py-1 rounded border text-[10px] font-bold uppercase tracking-wider ${getTypeBadgeColor(job.type)}`} data-id="element-1232">
                    {job.type}
                  </div>
                  <div className="flex items-center gap-1.5 text-sm font-medium text-navy" data-id="element-1233">
                    <Clock size={16} className="text-gray-400" data-id="element-1234" />
                    {job.timeStart} - {job.timeEnd}
                  </div>
                </div>
                <div data-id="element-1235">
                  <h3 className="text-base font-bold text-navy flex items-center gap-2" data-id="element-1236">
                    {job.address}
                  </h3>
                  <p className="text-sm text-gray-500 pl-0 mt-0.5" data-id="element-1237">
                    {job.city} • Host: {job.host}
                  </p>
                </div>
              </div>

              {/* Center: Assignment & Package */}
              <div className="lg:col-span-4 flex flex-col gap-3 lg:border-l lg:border-r lg:border-gray-100 lg:px-6" data-id="element-1238">
                <div className="flex items-center gap-3" data-id="element-1239">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${job.cleaner.avatarColor}`} data-id="element-1240">
                    {job.cleaner.initials}
                  </div>
                  <div data-id="element-1241">
                    <p className="text-xs text-gray-500" data-id="element-1242">Assegnato a</p>
                    <p className="text-sm font-medium text-navy" data-id="element-1243">
                      {job.cleaner.name}
                    </p>
                  </div>
                </div>
                {job.package && <div className="flex items-center gap-2 text-xs text-teal-deep bg-teal-50 px-3 py-1.5 rounded-lg w-fit" data-id="element-1244">
                    <Package size={14} data-id="element-1245" />
                    <span className="font-medium" data-id="element-1246">{job.package.name}</span>
                  </div>}
              </div>

              {/* Right Side: Status & Actions */}
              <div className="lg:col-span-4 flex items-center justify-between lg:justify-end gap-6" data-id="element-1247">
                <div className="flex flex-col items-end gap-1" data-id="element-1248">
                  {getStatusBadge(job.status)}
                  <span className="text-sm font-bold text-navy mt-1" data-id="element-1249">
                    {job.earnings}
                  </span>
                </div>

                <div className="flex items-center gap-2" data-id="element-1250">
                  <button className="p-2 text-gray-400 hover:text-navy hover:bg-gray-100 rounded-lg transition-colors" data-id="element-1251">
                    <MoreVertical size={20} data-id="element-1252" />
                  </button>
                  <button className="hidden sm:flex items-center gap-1 text-xs font-medium text-teal-deep hover:bg-teal-50 px-3 py-2 rounded-lg transition-colors" data-id="element-1253">
                    Dettagli <ArrowRight size={14} data-id="element-1254" />
                  </button>
                </div>
              </div>
            </div>
          </div>)}
      </div>
    </div>;
}